import { useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Check, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Reveal, RevealGroup, revealItem } from "@/components/Reveal";
import ProductRecommendation from "@/components/ProductRecommendation";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import CTABlock from "@/components/CTABlock";
import { DuckSays } from "@/components/DuckMascot";
import { DIY_CATEGORIES } from "@/data/products";

// A tiny transparent "DIY or Pro?" decision helper.
const QUESTIONS = [
  { q: "Is this a first-time, isolated sighting?", diy: "yes" },
  { q: "Has the problem come back after you've dealt with it before?", diy: "no" },
  { q: "Is there a nest or pest in a dangerous spot (high up, in a wall, near a door)?", diy: "no" },
  { q: "Could a simple change — dumping water, sealing a gap, trimming shade — plausibly fix it?", diy: "yes" },
  { q: "Does anyone in the home have a relevant allergy or health concern?", diy: "no" },
];

function DecisionTool() {
  const [answers, setAnswers] = useState({});
  const answered = Object.keys(answers).length;
  const diyScore = QUESTIONS.reduce((acc, q, i) => {
    if (answers[i] === undefined) return acc;
    return acc + (answers[i] === q.diy ? 1 : 0);
  }, 0);
  const done = answered === QUESTIONS.length;
  const leansDiy = diyScore >= 3;

  return (
    <div className="rounded-[2rem] border border-forest/10 bg-card p-6 sm:p-10" data-testid="decision-tool">
      <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-terracotta">Help me decide</span>
      <h2 className="mt-3 font-serif text-2xl sm:text-3xl font-bold text-forest-950">DIY or call a pro?</h2>
      <p className="mt-2 text-forest-950/70">Answer honestly — this is a nudge, not a verdict. You know your home best.</p>
      <div className="mt-6 space-y-3">
        {QUESTIONS.map((q, i) => (
          <div key={i} className="flex items-center justify-between gap-4 rounded-2xl bg-sand p-4">
            <span className="text-[15px] text-forest-950/85">{q.q}</span>
            <div className="flex gap-2 shrink-0">
              {["yes", "no"].map((val) => (
                <button
                  key={val}
                  onClick={() => setAnswers((a) => ({ ...a, [i]: val }))}
                  className={`px-4 py-2 rounded-full text-sm font-semibold capitalize transition-all ${
                    answers[i] === val ? "bg-forest-950 text-cream" : "bg-cream border border-forest/15 text-forest-950/70 hover:border-forest/30"
                  }`}
                  data-testid={`decision-q${i}-${val}`}
                >
                  {val}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <AnimatePresence>
        {done && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6" data-testid="decision-result">
            <div className={`rounded-2xl p-6 ${leansDiy ? "bg-duckgreen/12 border border-duckgreen/30" : "bg-terracotta/10 border border-terracotta/30"}`}>
              <div className="flex items-center gap-3">
                {leansDiy ? <Check className="w-6 h-6 text-forest" /> : <X className="w-6 h-6 text-terracotta" />}
                <h3 className="font-serif text-xl font-bold text-forest-950">
                  {leansDiy ? "This leans DIY-friendly." : "This leans toward calling a pro."}
                </h3>
              </div>
              <p className="mt-2 text-sm text-forest-950/75 leading-relaxed">
                {leansDiy
                  ? "Based on your answers, it's worth trying prevention and DIY steps first. Browse the categories below to get started — you can always call a pro if it doesn't hold."
                  : "Based on your answers, professional help is a reasonable next step. That said, prevention still matters — a pro will thank you for it. Get matched below."}
              </p>
              <Link to={leansDiy ? "#categories" : "/hire-a-pro"} className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-forest-950 text-cream font-semibold hover:bg-forest transition-all" data-testid="decision-cta">
                {leansDiy ? "Show me DIY steps" : "Find a Pro"} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function DIY() {
  return (
    <div data-testid="diy-page">
      <Seo
        title="DIY Pest Control Hub"
        description="Prevention, backyard mosquito control, tick prevention, standing water, traps and tools, and more — the DuckTheBugs DIY resource hub for homeowners."
        path="/diy"
      />
      <section className="bg-forest-950 text-cream grain pt-[72px]">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-24">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "DIY" }]} />
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] max-w-3xl">
            DIY first? We'll show you where to start.
          </h1>
          <p className="mt-5 text-lg text-cream/80 max-w-2xl leading-relaxed">
            The cheapest, safest pest control is often the problem you prevent. Here's the practical stuff — much of it free, most of it chemical-free, all of it homeowner-tested thinking.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-20">
        <Reveal>
          <DecisionTool />
        </Reveal>
      </section>

      <section id="categories" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-16 md:pb-20 scroll-mt-24">
        <Reveal>
          <div className="mb-10">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-terracotta">The playbook</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-bold text-forest-950">DIY categories</h2>
          </div>
        </Reveal>
        <RevealGroup className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {DIY_CATEGORIES.map((cat) => {
            const Icon = Icons[cat.icon] || Icons.Leaf;
            return (
              <motion.div key={cat.slug} variants={revealItem} className="group rounded-3xl border border-forest/10 bg-card p-6 hover:shadow-lg hover:shadow-forest/5 hover:-translate-y-1 transition-all" data-testid={`diy-category-${cat.slug}`}>
                <div className="w-12 h-12 rounded-2xl bg-duckgreen/12 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-forest" />
                </div>
                <h3 className="font-serif text-xl font-bold text-forest-950">{cat.name}</h3>
                <p className="mt-2 text-sm text-forest-950/70 leading-relaxed">{cat.body}</p>
              </motion.div>
            );
          })}
        </RevealGroup>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-16 md:pb-20">
        <Reveal>
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-terracotta">Editorial picks</span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-bold text-forest-950">Tools worth knowing about</h2>
            </div>
          </div>
          <AffiliateDisclosure />
        </Reveal>
        <RevealGroup className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {["outdoor-fan", "yard-barrier", "ant-bait", "gutter-guard", "flea-vacuum", "caulk-sealant"].map((s) => (
            <motion.div key={s} variants={revealItem}>
              <ProductRecommendation slug={s} />
            </motion.div>
          ))}
        </RevealGroup>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-16 md:pb-20">
        <div className="rounded-[2rem] bg-sand p-8 sm:p-10">
          <DuckSays label="Duck's rule of thumb">
            If a problem keeps coming back after you've genuinely addressed the cause, that's the signal to bring in a
            pro. DIY is about the cause; a pro is for when the cause is bigger than a weekend.
          </DuckSays>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-20 md:pb-28">
        <CTABlock
          variant="dark"
          eyebrow="When DIY isn't enough"
          title="Tried the basics and still bugged?"
          body="No shame in it — some problems are just bigger than a flowerpot. Tell us what's going on and we'll help you find an appropriate local pro."
          primary={{ to: "/hire-a-pro", label: "Find a Pro" }}
          secondary={{ to: "/guides", label: "Read the guides" }}
        />
      </div>
    </div>
  );
}
